import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import LightboxButton from "../../components/lightbox/LightboxButton";
import type { Game } from "../../types/game";
import styles from "./CharacterReveal.module.scss";

export interface CharacterRevealProps {
  character: Game["my_character"];
  hostNotPlaying?: boolean;
  gameId?: string;
  /* UI-DESIGN: inside a glass panel already (lobby) it drops its own box */
  inline?: boolean;
  compact?: boolean;
  /* UI-DESIGN: one red action per screen; red only when revealing is the next step */
  emphasis?: boolean;
}

// UI-DESIGN: mount with a game/character/role key. Nothing identifying the role
// enters the rendered page until its owner deliberately opens the envelope.
export default function CharacterReveal({ character, hostNotPlaying = false, gameId, inline = false, compact = false, emphasis = false }: CharacterRevealProps) {
  const [opened, setOpened] = useState(false);
  const navigate = useNavigate();
  const heading = useRef<HTMLHeadingElement>(null);
  const revealButton = useRef<HTMLButtonElement>(null);
  const previouslyOpened = useRef(false);
  // UI-DESIGN: move focus only when opening/closing, never on a game refresh.
  useEffect(() => {
    if (opened) heading.current?.focus();
    else if (previouslyOpened.current) revealButton.current?.focus();
    previouslyOpened.current = opened;
  }, [opened]);
  const role = character?.role;
  const accessCode = character?.access_code;
  if (hostNotPlaying) return null;
  const box = (state: string | false) => clsx(styles.paper, inline && styles.inline, compact && styles.compact, state);

  if (!role) return (
    <section className={box(styles.waiting)} aria-labelledby="character-heading">
      <h2 id="character-heading">Your character is on its way</h2>
      <p>Your role isn’t available yet. Check with your host, then refresh the game.</p>
    </section>
  );

  return (
    <section className={box(opened && styles.open)} aria-labelledby="character-heading">
      {!opened ? (
        <>
          <div className={styles.envelope} aria-hidden="true"><span /></div>
          <div className={styles.copy}>
            <h2 id="character-heading">{compact ? "Your character" : "A part just for you"}</h2>
            <p>{compact ? "For your eyes only." : "Open it when you’re ready. Keep your secrets."}</p>
          </div>
          <LightboxButton ref={revealButton} className={styles.openButton} tone={emphasis ? "primary" : "secondary"} onPress={() => setOpened(true)}>Reveal my character</LightboxButton>
        </>
      ) : (
        <>
          <h2 id="character-heading" tabIndex={-1} ref={heading}>{role.name || "Your character"}</h2>
          {(role.nationality || role.date_of_birth) && <p className={styles.details}>{[role.nationality, role.date_of_birth && `Born ${role.date_of_birth}`].filter(Boolean).join(", ")}</p>}
          <p className={styles.description}>{role.character_info?.trim() || role.description?.trim() || "Open your character sheet to get to know your part."}</p>
          {accessCode ? (
            <LightboxButton className={styles.openButton} tone="primary" onPress={() => navigate(`/passport?${new URLSearchParams({ access_code: accessCode, ...(gameId ? { gameId } : {}) }).toString()}`)}>Open character sheet</LightboxButton>
          ) : <p role="status">Your character sheet isn’t available yet. Check with your host.</p>}
          <button type="button" className={styles.hideButton} onClick={() => setOpened(false)}>Close my file</button>
        </>
      )}
    </section>
  );
}
